<template>
  <footer class="footer-section" role="contentinfo" aria-label="تذييل الصفحة">
    <div class="container">
      <div class="row">
        <div class="col-lg-4 footer-column">
          <h5 class="footer-title">الرحيمة</h5>
          <p class="footer-description">
            {{ settings.footerDescription || 'منصة تعليمية إسلامية تهدف إلى نشر القرآن الكريم والعلم الشرعي والمحتوى الهادف بأسلوب بسيط يتناسب مع كل الفئات مع الإلتزام التام بالهوية الإسلامية.' }}
          </p>
          <div class="social-links">
            <a v-if="settings.socialFacebook" :href="settings.socialFacebook" target="_blank" rel="noopener noreferrer" class="social-link" aria-label="تابعنا على فيسبوك">
              <i class="bi bi-facebook"></i>
            </a>
            <a v-if="settings.socialWhatsapp" :href="'https://wa.me/' + settings.socialWhatsapp" target="_blank" rel="noopener noreferrer" class="social-link" aria-label="تواصل معنا على واتساب">
              <i class="bi bi-whatsapp"></i>
            </a>
            <a v-if="settings.socialInstagram" :href="settings.socialInstagram" target="_blank" rel="noopener noreferrer" class="social-link" aria-label="تابعنا على انستغرام">
              <i class="bi bi-instagram"></i>
            </a>
            <a v-if="settings.socialYoutube" :href="settings.socialYoutube" target="_blank" rel="noopener noreferrer" class="social-link" aria-label="اشترك في قناتنا على يوتيوب">
              <i class="bi bi-youtube"></i>
            </a>
            <a v-if="settings.socialTiktok" :href="settings.socialTiktok" target="_blank" rel="noopener noreferrer" class="social-link" aria-label="تابعنا على تيك توك">
              <i class="bi bi-tiktok"></i>
            </a>
          </div>
        </div>

        <div class="col-lg-2 footer-column">
          <h6 class="footer-subtitle">روابط سريعة</h6>
          <ul class="footer-links">
            <li>
              <a href="/privacy-policy" aria-label="الذهاب إلى سياسة الخصوصية">
                <span class="link-text">سياسة الخصوصية</span>
                <i class="bi bi-chevron-left chevron-icon"></i>
              </a>
            </li>
            <li>
              <a href="/terms-of-use" aria-label="الذهاب إلى شروط الاستخدام">
                <span class="link-text">شروط الاستخدام</span>
                <i class="bi bi-chevron-left chevron-icon"></i>
              </a>
            </li>
            <li>
              <a href="/fatwa" aria-label="الذهاب إلى صفحة الأسئلة والفتاوى">
                <span class="link-text">الأسئلة والفتاوى</span>
                <i class="bi bi-chevron-left chevron-icon"></i>
              </a>
            </li>
            <li>
              <a href="/hisn-al-muslim" aria-label="الذهاب إلى حصن المسلم">
                <span class="link-text">حصن المسلم</span>
                <i class="bi bi-chevron-left chevron-icon"></i>
              </a>
            </li>
            <li>
              <a href="/videos" aria-label="الذهاب إلى صفحة الفيديوهات">
                <span class="link-text">الفيديوهات</span>
                <i class="bi bi-chevron-left chevron-icon"></i>
              </a>
            </li>
            <li>
              <a href="/courses" aria-label="الذهاب إلى صفحة الدورات">
                <span class="link-text">الدورات</span>
                <i class="bi bi-chevron-left chevron-icon"></i>
              </a>
            </li>
          </ul>
        </div>

        <div class="col-lg-3 footer-column">
          <h6 class="footer-subtitle">تواصل معنا</h6>
          <ul class="contact-info">
            <li>
              <div class="contact-item">
                <i class="bi bi-envelope contact-icon" aria-hidden="true"></i>
                <span class="contact-text">{{ settings.contactEmail }}</span>
              </div>
            </li>
            <li>
              <div class="contact-item">
                <i class="bi bi-phone contact-icon" aria-hidden="true"></i>
                <span class="contact-text">{{ settings.contactPhone }}</span>
              </div>
            </li>
            <li>
              <div class="contact-item">
                <i class="bi bi-geo-alt contact-icon" aria-hidden="true"></i>
                <span class="contact-text">{{ settings.address || 'السودان' }}</span>
              </div>
            </li>
          </ul>
        </div>

        <div class="col-lg-3 footer-column">
          <h6 class="footer-subtitle" id="newsletter-label">النشرة البريدية</h6>
          <p>إشترك ليصلك كل جديد</p>
          <form @submit.prevent="subscribeNewsletter" class="newsletter-form" role="form" aria-labelledby="newsletter-label">
            <input
                type="text"
                placeholder="أدخل البريد أو رقم الواتساب"
                class="newsletter-input"
                aria-label="البريد الإلكتروني أو الواتساب للاشتراك في النشرة"
                required
                v-model="contactValue">
            <button
                type="submit"
                class="newsletter-btn fw-bold"
                aria-label="إرسال الاشتراك"
                :disabled="isSubmitting">
              <span v-if="isSubmitting">جاري...</span>
              <span v-else>إرسال</span>
            </button>
          </form>
          <div v-if="subscriptionMessage" class="subscription-message" role="alert">
            {{ subscriptionMessage }}
          </div>
        </div>
      </div>

      <div class="footer-bottom mb-5">
        <p>&copy; <span id="current-year" aria-live="polite">{{ currentYear }}</span> جميع الحقوق محفوظة للرحيمة</p>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import api from '@/api'

const currentYear = ref('')
const subscriptionMessage = ref('')
const isSubmitting = ref(false)
const contactValue = ref('')
const router = useRouter()
const store = useStore()

const settings = computed(() => store.getters['settings/getSettings'])

// تحديث السنة الحالية تلقائياً
onMounted(() => {
  currentYear.value = new Date().getFullYear()
  if (!store.getters['settings/isLoaded']) {
    store.dispatch('settings/fetchPublicSettings')
  }
})

// دالة الاشتراك في النشرة البريدية
const subscribeNewsletter = async () => {
  const value = contactValue.value.trim()
  if (!value) return;

  let payload = {};
  if (value.includes('@')) {
    if (!isValidEmail(value)) {
      subscriptionMessage.value = 'يرجى إدخال بريد إلكتروني صحيح'
      return;
    }
    payload.email = value;
  } else {
    // Assume it's a phone number (WhatsApp)
    if (value.length < 8) {
       subscriptionMessage.value = 'يرجى إدخال رقم هاتف صحيح'
       return;
    }
    payload.whatsapp = value;
  }

  if (isSubmitting.value) return
  isSubmitting.value = true
  subscriptionMessage.value = 'جاري الإرسال...'

  try {
    const response = await api.post('/newsletter/subscribe', payload);
    subscriptionMessage.value = response.data.message || 'شكراً لإشتراكك في النشرة البريدية';
    contactValue.value = ''
    setTimeout(() => { subscriptionMessage.value = '' }, 5000)
  } catch (error) {
    const errorMsg = error.response?.data?.message || 'حدث خطأ، يرجى المحاولة مرة أخرى.';
    subscriptionMessage.value = errorMsg;
    setTimeout(() => { subscriptionMessage.value = '' }, 3000)
  } finally {
    isSubmitting.value = false
  }
}

// التحقق من صحة البريد الإلكتروني
const isValidEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return re.test(email)
}
</script>

<style scoped>
.footer-section {
  background: var(--nav-bg);
  color: var(--text-muted);
  padding: 60px 0 30px;
  border-top: 1px solid var(--border-color);
  contain: content;
  transform: translateZ(0);
  backface-visibility: hidden;
  transition: background 0.3s ease, border-color 0.3s ease;
}

.footer-column {
  margin-bottom: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.footer-title {
  color: var(--accent-gold);
  font-size: 1.8rem;
  margin: 0 0 15px 0;
  font-weight: 700;
  text-align: center;
}

.footer-description {
  color: var(--text-muted);
  line-height: 1.6;
  margin-bottom: 15px;
  text-align: center;
  max-width: 500px;
}

.social-links {
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-top: 5px;
}

.social-link {
  width: 40px;
  height: 40px;
  background: var(--glass-panel);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  text-decoration: none;
  transition: all 0.3s ease;
  transform: translateZ(0);
  will-change: transform;
}

.social-link:hover,
.social-link:focus {
  background: var(--accent-gold);
  color: #fff !important;
  transform: translateY(-3px);
  text-decoration: none;
}

.footer-subtitle {
  color: var(--accent-gold);
  font-size: 1.3rem;
  margin: 0 0 15px 0;
  font-weight: 600;
  text-align: center;
  width: 100%;
}

.footer-links {
  list-style: none;
  padding: 0;
  width: 100%;
}

.footer-links li {
  margin-bottom: 12px;
  display: flex;
  justify-content: center;
}

.footer-links a {
  color: var(--text-muted);
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
  will-change: transform;
  flex-direction: row-reverse;
  width: fit-content;
  padding: 5px 10px;
  border-radius: 4px;
}

.footer-links a:hover {
  color: var(--accent-gold);
  gap: 12px;
}

.link-text {
  text-align: center;
}

.chevron-icon {
  transition: transform 0.3s ease;
}

.footer-links a:hover .chevron-icon {
  transform: translateX(-4px);
}

.contact-info {
  list-style: none;
  padding: 0;
  width: 100%;
}

.contact-info li {
  margin-bottom: 10px;
  display: flex;
  justify-content: center;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--text-muted);
  transition: all 0.3s ease;
  will-change: transform;
  width: 100%;
  max-width: 250px;
}

.contact-icon {
  color: var(--accent-gold);
  font-size: 1.5rem;
  flex-shrink: 0;
  width: 24px;
  text-align: center;
}

.contact-text {
  font-size: 0.95rem;
  line-height: 1.4;
  text-align: right;
  flex: 1;
}

.newsletter-form {
  display: flex;
  margin-top: 15px;
  width: 100%;
  max-width: 350px;
  justify-content: center;
}

.newsletter-input {
  flex: 1;
  padding: 12px 15px;
  border: 1px solid var(--border-color);
  background: var(--glass-panel);
  color: var(--text-color);
  outline: none;
  text-align: center;
  transition: var(--transition);
  border-radius: 0 var(--border-radius) var(--border-radius) 0;
}

.newsletter-input:focus {
  border-color: var(--accent-gold);
  box-shadow: 0 0 0 2px rgba(212, 175, 55, 0.2);
}

.newsletter-btn {
  background: var(--accent-gold);
  color: #0b061d;
  border: none;
  padding: 0 25px;
  border-radius: var(--border-radius) 0 0 var(--border-radius);
  cursor: pointer;
  transition: all 0.3s ease;
  transform: translateZ(0);
  will-change: transform;
  min-width: 100px;
  font-size: 1rem;
}

.newsletter-btn:hover:not(:disabled) {
  background-color: #d4af37 !important;
  box-shadow: 0px 4px 12px var(--btn-hover-color);
  color: var(--footer-grad-end) !important;
}

.newsletter-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.footer-bottom {
  text-align: center;
  color: var(--text-muted);
  height: 1px;
  background: linear-gradient(to right, transparent, var(--border-color), transparent);
  margin-top: 30px;
}

.footer-bottom p {
  padding-top: 2rem;
  font-size: 0.9rem;
}

/* رسالة الاشتراك */
.subscription-message {
  margin-top: 1rem;
  padding: 0.75rem;
  background: rgba(212, 175, 55, 0.1);
  border-radius: 8px;
  color: var(--accent-gold);
  text-align: center;
  animation: fadeIn 0.3s ease;
  max-width: 350px;
  width: 100%;
  font-size: 0.9rem;
  border: 1px solid rgba(212, 175, 55, 0.2);
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* تحسين التباين للشاشات ذات الإضاءة المنخفضة */
@media (prefers-contrast: high) {
  .footer-section {
    background: #1a1a2e;
    border-top: 3px solid var(--accent-gold);
  }

  .social-link {
    border: 2px solid var(--accent-gold);
  }
}

/* تحسين الأداء - تقليل تحريك الشاشة */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }

  .footer-links a,
  .social-link,
  .newsletter-btn {
    transition: none;
  }

  .subscription-message {
    animation: none;
  }
}

/* تصميم متجاوب */
@media (max-width: 768px) {
  .footer-column {
    text-align: center;
    margin-bottom: 2.5rem;
    align-items: center;
  }

  .social-links {
    flex-direction: row !important;
    justify-content: center;
    margin-top: 1rem;
  }

  .footer-links,
  .contact-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .footer-links li,
  .contact-info li {
    justify-content: center;
    width: 100%;
  }

  .footer-links a {
    flex-direction: column;
    gap: 5px;
  }

  .chevron-icon {
    display: none;
  }

  /* تحسين توسيط عناصر تواصل معنا */
  .contact-item {
    flex-direction: column;
    align-items: center;
    gap: 5px;
    max-width: 100%;
  }

  .contact-icon {
    margin-bottom: 5px;
  }

  .contact-text {
    text-align: center;
    font-size: 0.9rem;
    line-height: 1.3;
  }

  /* زيادة التباعد بين عناصر التواصل في الشاشات الصغيرة */
  .contact-info li {
    margin-bottom: 15px;
  }

  .newsletter-form {
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    max-width: 300px;
  }

  .newsletter-input {
    width: 100%;
    border-radius: 25px;
    margin-bottom: 0;
    text-align: center;
    border-radius: var(--border-radius);
  }

  .newsletter-btn {
    border-radius: 25px;
    padding: 0.75rem 2rem;
    width: auto;
    border-radius: var(--border-radius);
  }

  .footer-title {
    font-size: 1.5rem;
    margin-bottom: 15px;
  }

  .footer-subtitle {
    font-size: 1.1rem;
    margin-bottom: 15px;
  }

  .footer-description {
    font-size: 0.95rem;
    padding: 0 1rem;
    margin-bottom: 15px;
  }

  /* توسيط جميع العناصر */
  .footer-column > * {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    width: 100%;
  }

  .footer-bottom {
    margin-top: 20px;
  }
}

/* تحسينات للشاشات المتوسطة */
@media (min-width: 769px) and (max-width: 992px) {
  .footer-column {
    margin-bottom: 2rem;
    align-items: flex-start;
  }

  .footer-title {
    font-size: 1.6rem;
    text-align: right;
    margin: 0 0 15px 0;
  }

  .footer-description {
    text-align: right;
  }

  .social-links {
    justify-content: flex-start;
  }

  .footer-subtitle {
    text-align: right;
    margin: 0 0 15px 0;
  }

  .footer-links li {
    justify-content: flex-start;
  }

  .contact-info li {
    justify-content: flex-start;
  }

  .contact-item {
    flex-direction: row;
    justify-content: flex-start;
  }

  .contact-text {
    text-align: right;
  }

  .newsletter-form {
    justify-content: flex-start;
  }
}

/* تحسين للشاشات الكبيرة جداً */
@media (min-width: 1400px) {
  .container {
    max-width: 1320px;
  }

  .footer-section {
    padding: 80px 0 40px;
  }
}

/* شاشات كبيرة (للعودة إلى التصميم الأصلي) */
@media (min-width: 993px) {
  .footer-column {
    align-items: flex-start;
  }

  .footer-title,
  .footer-description,
  .footer-subtitle {
    text-align: right;
  }

  .footer-title {
    margin: 0 0 15px 0;
  }

  .footer-subtitle {
    margin: 0 0 15px 0;
  }

  .social-links {
    justify-content: flex-start;
  }

  .footer-links li {
    justify-content: flex-start;
  }

  .footer-links a {
    justify-content: flex-start;
  }

  .contact-info li {
    justify-content: flex-start;
  }

  .contact-item {
    flex-direction: row;
    text-align: right;
  }

  .newsletter-form {
    justify-content: flex-start;
  }
}
</style>